import React, { useMemo, useState } from 'react'
import {
  FlatList,
  Image,
  StyleSheet,
  TouchableOpacity,
  View,
  ActivityIndicator,
} from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, Icon, BottomModalBase, ContactItemSkeletonList } from '@components'
import { Swipeable, ActionSwipeable } from '@components'
import { SocialNetworkSection } from '../../../../screens/profile/components'
import { Contact, useLoadAllPages, usePaginateKeepContacts } from '@hooks'
import { defaultImage } from '@assets'

import { useLanguage } from '@context'
import { useCreationStackIntegration } from '@hooks'
import { formatRecurrenceLabel } from '@utils'
import { RelationshipStatusButton } from './RelationshipStatusButton'
import { QuickKeepContactDropdown } from '../../../contacts/detailsGhostProfile/components/QuickKeepContactDropdown'

type ContactsListProps = {
  contacts: Contact[]
  onContactPress?: (contact: Contact) => void
  onEditContact?: (contact: Contact) => void
  onDeleteContact?: (contact: Contact) => void
  onMessageContact?: (contact: Contact) => void
  emptyMessage?: string
  isLoading?: boolean
  enableSwipe?: boolean
  noRightSection?: boolean
  selectedContactId?: string
  hasNextPage?: boolean
  isFetchingNextPage?: boolean
  isFetching?: boolean
  fetchNextPage?: () => void
}

const AVATAR_SIZE = 50
const ITEM_HEIGHT = 78

export const ContactsList: React.FC<ContactsListProps> = ({
  contacts,
  onContactPress,
  onEditContact,
  onDeleteContact,
  onMessageContact,
  emptyMessage,
  isLoading = false,
  enableSwipe = true,
  noRightSection = false,
  selectedContactId,
  hasNextPage = false,
  isFetchingNextPage = false,
  isFetching = false,
  fetchNextPage,
}) => {
  const theme = useTheme()
  const styles = getStyle(theme)

  const navigation = useNavigation()
  const { t } = useLanguage()

  const { showKeepContactCreation, isCreationStackEnabled } = useCreationStackIntegration()
  const keepContactsQuery = usePaginateKeepContacts()
  const { flatData: keepContacts } = keepContactsQuery
  // Every follow-up, so each contact of the list shows whether it has one.
  useLoadAllPages(keepContactsQuery)

  const keepContactMap = useMemo(() => {
    const map = new Map<string, { id: string; rrule?: string | null; keepDate: string }>()
    for (const kc of keepContacts) {
      map.set(kc.contactAccountId, { id: kc.id, rrule: kc.rrule, keepDate: kc.keepDate })
    }
    return map
  }, [keepContacts])

  const [selectedContact, setSelectedContact] = useState<Contact>()
  const [keepContactDropdownContact, setKeepContactDropdownContact] = useState<Contact | null>(null)

  const getSwipeActions = (contact: Contact): ActionSwipeable[] => {
    const actions: ActionSwipeable[] = []

    if (onEditContact) {
      actions.push({
        icon: 'edit',
        title: t('common.edit'),
        color: theme.colors.primary,
        onPress: () => onEditContact(contact),
      })
    }

    if (onDeleteContact) {
      actions.push({
        icon: 'trash',
        title: t('common.delete'),
        color: theme.colors.error,
        onPress: () => onDeleteContact(contact),
      })
    }

    return actions
  }

  const goToProfile = (contact: Contact) => {
    ;(navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contact.id },
    })
  }

  const goToKeepContactFull = (contact: Contact) => {
    const keepInfo = keepContactMap.get(contact.accountId)
    if (isCreationStackEnabled) {
      showKeepContactCreation({
        isCreationStack: true,
        keepContactId: keepInfo?.id,
        contactId: contact.accountId,
      })
    } else {
      ;(navigation as any).navigate('Menu', {
        screen: 'KeepContact',
        params: {
          screen: 'AddKeep',
          params: {
            keepContactId: keepInfo?.id,
            contactId: contact.id,
          },
        },
      })
    }
  }

  const renderContactItem = ({ item: contact }: { item: Contact }) => {
    const isGhost = contact.isGhost
    const keepInfo = keepContactMap.get(contact.accountId)
    const frequencyLabel = keepInfo
      ? formatRecurrenceLabel(keepInfo.rrule, keepInfo.keepDate, t)
      : null

    const contactContent = (
      <TouchableOpacity
        style={[
          styles.contactItem,
          selectedContactId === contact.id && { backgroundColor: theme.colors.grey10 },
        ]}
        onPress={() => onContactPress?.(contact)}
        activeOpacity={0.7}
      >
        <View style={styles.leftSection}>
          <TouchableOpacity onPress={() => goToProfile(contact)}>
            <Image
              source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
              style={styles.avatar}
            />
          </TouchableOpacity>
          {/* Badge fantôme : distingue un profil fantôme d'un contact réel. */}
          {isGhost && (
            <View style={styles.ghostBadge} pointerEvents="none">
              <Icon name="ghost" size={10} color={theme.colors.purple} />
            </View>
          )}
        </View>

        <View style={styles.middleSection}>
          <Text type="bodySmallBold" numberOfLines={1}>
            {contact.fullName}
          </Text>
          {contact.professions && contact.professions.length > 0 && (
            <Text type="bodyExtraSmall" color={theme.colors.secondary} numberOfLines={1}>
              {contact.professions.map((p) => p.name).join(' & ')}
            </Text>
          )}
          {/* Hashtags */}
          {contact.hashtags && contact.hashtags.length > 0 && (
            <Text
              type="bodyExtraSmall"
              color={theme.colors.primary}
              numberOfLines={1}
              style={styles.hashtags}
            >
              {contact.hashtags.join(' ')}
            </Text>
          )}

          {/* Fréquence garder contact */}
          {frequencyLabel && frequencyLabel !== 'Jamais' && (
            <TouchableOpacity
              style={styles.frequencyRow}
              onPress={() => setKeepContactDropdownContact(contact)}
            >
              <Icon name="keepContact" size={10} color={theme.colors.primary} />
              <Text type="bodyExtraSmall" color={theme.colors.primary} numberOfLines={1}>
                {frequencyLabel}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {/* Section droite : message, réseaux sociaux, puis évolution de la
            relation placée tout à droite (comme historiquement). */}
        {!noRightSection && (
          <View style={styles.rightSection}>
            <TouchableOpacity
              style={isGhost && styles.messageButtonDisabled}
              onPress={() => onMessageContact?.(contact)}
              disabled={isGhost}
              activeOpacity={0.7}
            >
              <Icon name="messageCircle" size={24} color={theme.colors.primary} />
            </TouchableOpacity>
            <View style={styles.socialNetworkSection}>
              <SocialNetworkSection
                isSmall
                onPress={() => setSelectedContact(contact)}
                facebook={contact.contacts?.facebook}
                instagram={contact.contacts?.instagram}
                linkedin={contact.contacts?.linkedin}
                twitter={contact.contacts?.twitter}
                searchName={contact.fullName}
              />
            </View>
            <View style={styles.relationshipStatusWrapper}>
              <RelationshipStatusButton
                contactId={contact.id}
                relationshipStatusId={contact.relationshipStatusId}
              />
            </View>
          </View>
        )}
      </TouchableOpacity>
    )

    if (enableSwipe) {
      return <Swipeable actions={getSwipeActions(contact)}>{contactContent}</Swipeable>
    }

    return contactContent
  }

  const renderEmptyState = () => (
    <View style={styles.emptyState}>
      <Text type="bodySmallBold" color={theme.colors.secondary}>
        {emptyMessage || t('contactsList.noContactFound')}
      </Text>
    </View>
  )

  const renderSeparator = () => <View style={styles.separator} />

  if (isLoading) {
    return <ContactItemSkeletonList count={8} />
  }

  return (
    <>
      <FlatList
        data={contacts}
        renderItem={renderContactItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={renderSeparator}
        // Only for a screen that gives its message: the Notes column and the
        // map reuse this list without always passing their loading state.
        ListEmptyComponent={emptyMessage ? renderEmptyState : null}
        contentContainerStyle={[styles.container, { flexGrow: 1 }]}
        showsVerticalScrollIndicator={false}
        getItemLayout={(_, index) => ({
          length: ITEM_HEIGHT + 1, // + 1px separator
          offset: (ITEM_HEIGHT + 1) * index,
          index,
        })}
        removeClippedSubviews
        maxToRenderPerBatch={10}
        windowSize={10}
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
            fetchNextPage()
          }
        }}
        onEndReachedThreshold={0.5}
        ListFooterComponent={isFetchingNextPage ? <ActivityIndicator size="large" /> : null}
      />
      <BottomModalBase
        isVisible={!!selectedContact}
        setIsVisible={() => setSelectedContact(undefined)}
        hasCloseButton
        largePaddingTop={false}
        wrapContent
      >
        <View style={styles.modalContainer}>
          <Text type="bodyBold">{t('contactsList.contactViaSocial')}</Text>
          <SocialNetworkSection
            isList
            facebook={selectedContact?.contacts?.facebook}
            instagram={selectedContact?.contacts?.instagram}
            twitter={selectedContact?.contacts?.twitter}
            linkedin={selectedContact?.contacts?.linkedin}
            searchName={selectedContact?.fullName}
          />
        </View>
      </BottomModalBase>
      {keepContactDropdownContact && (
        <QuickKeepContactDropdown
          visible={!!keepContactDropdownContact}
          onClose={() => setKeepContactDropdownContact(null)}
          contactAccountId={keepContactDropdownContact.accountId}
          contactName={keepContactDropdownContact.fullName}
          existingKeepContact={
            keepContacts.find(
              (kc) => kc.contactAccountId === keepContactDropdownContact.accountId,
            ) ?? null
          }
          onCustomize={() => goToKeepContactFull(keepContactDropdownContact)}
        />
      )}
    </>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexGrow: 1,
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.xxl,
    },
    contactItem: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: theme.spacing.md,
      height: ITEM_HEIGHT,
    },
    leftSection: {
      position: 'relative',
      marginRight: theme.spacing.md,
    },
    avatar: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.colors.background,
    },
    ghostBadge: {
      position: 'absolute',
      right: 0,
      bottom: 0,
      width: 16,
      height: 16,
      borderRadius: 8,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.purple,
      alignItems: 'center',
      justifyContent: 'center',
    },
    hashtags: {
      marginTop: theme.spacing.xs / 2,
    },
    middleSection: {
      flex: 1,
      justifyContent: 'center',
    },
    frequencyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 4,
      marginTop: 2,
    },
    rightSection: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: theme.spacing.md,
    },
    // Fixed-size, centered slot at the far right of the row so the relationship
    // icon stays aligned and a little spaced from the social icons on its left,
    // whatever icon is shown.
    relationshipStatusWrapper: {
      width: 24,
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: theme.spacing.lg,
    },
    socialNetworkSection: {
      marginHorizontal: theme.spacing.md,
    },
    messageButtonDisabled: {
      opacity: 0,
    },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: theme.spacing.xxl,
    },
    separator: {
      height: 1,
      backgroundColor: theme.colors.grey,
    },
    modalContainer: {
      width: '100%',
    },
  })
